import React, { useState } from 'react';
import DataTable from 'react-data-table-component';
import './TauriAdvancedTableVisual.css';
import setting from '../../config.json';
import { CSVLink } from 'react-csv';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import TauriSectionModalVisual from '../TauriSectionBaseVisual/TauriSectionModalVisual/TauriSectionModalVisual';
import TauriSectionModalMultiVisual from '../TauriSectionBaseVisual/TauriSectionModalMultiVisual/TauriSectionModalMultiVisual';
import TauriCustomPaganation from '../TauriCustomPaganation/TauriCustomPaganation';

const TauriAdvancedTableVisual = ({ tableConfig, tableData, tableOptions, columnSize, rowSize, palettes, themes, chartPalettes, onLoadData, paginationProps }) => {

    const [selectedVisual, setSelectedVisual] = useState(null);
    const [selectedData, setSelectedData] = useState(null);
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [exportToggleOpen, setExportToggleOpen] = useState(false);

    const {
        currentPage,
        totalPages,
        onPageChange,
        displayPerPage,
        onDisplayPerPage,
        totalItems,
        actualData
    } = paginationProps;

    const exportToggle = () => setExportToggleOpen(!exportToggleOpen);

    const calculateTotals = () => {
        const totals = {};

        // Check if pagination and onLoadData are present
        const useOnLoadData = tableConfig.binding.data?.pagination?.show && onLoadData;

        tableConfig.binding.data.columns.forEach((column, index) => {
            const columnName = column.columnName;
            const firstValue = tableData.length > 0 ? tableData[0][columnName] : null;

            if (index === 0) {
                // For the first column, display "Total :"
                totals[columnName] = 'Total :';
            } else if (column.sum && typeof firstValue === 'number') {
                if (useOnLoadData && onLoadData[columnName] !== undefined) {
                    // Use onLoadData for totals if pagination is enabled
                    totals[columnName] = onLoadData[columnName];
                } else {
                    // Fallback to calculating totals from tableData
                    totals[columnName] = tableData.reduce((acc, row) => {
                        const value = row[columnName];
                        if (typeof value === 'number' && !isNaN(value)) {
                            return acc + value;
                        } else {
                            return acc;
                        }
                    }, 0);
                }
            } else {
                // For other columns, leave totals empty
                totals[columnName] = '';
            }
        });

        return totals;
    };

    const calculateSubTotalTotals = () => {
        const totals = {};

        tableConfig.binding.data.columns.forEach((column, index) => {
            const columnName = column.columnName;
            const firstValue = tableData.length > 0 ? tableData[0][columnName] : null;

            if (index === 0) {
                totals[columnName] = 'Sub Total :';
            } else if (column.sum && typeof firstValue === 'number') {
                totals[columnName] = tableData.reduce((acc, row) => {
                    const value = row[columnName];
                    if (typeof value === 'number' && !isNaN(value)) {
                        return acc + value;
                    } else {
                        return acc;
                    }
                }, 0);
            } else {
                totals[columnName] = '';
            }
        });

        return totals;
    };

    // Check if any column has sum set to true
    const hasSummableColumn = tableConfig.binding.data.columns.some(column => column.sum);

    const totalsRow = hasSummableColumn ? calculateTotals() : null;
    const subTotalsRow = hasSummableColumn ? calculateSubTotalTotals() : null;

    const tableDataWithTotals = totalsRow
        ? tableConfig.binding.data?.subtotal
            ? [...tableData, subTotalsRow, totalsRow]
            : [...tableData, totalsRow]
        : tableData;

    const columns = tableConfig.binding.data.columns.map(column => ({
        name: column.name,
        selector: row => {
            let value = row[column.columnName];

            if (typeof value === 'object' || value === null) {
                value = '';
            }

            const isTotalRow = row === totalsRow || row === subTotalsRow; // Check if the row is the totalsRow
            const isNegative = typeof value === 'number' && value < 0;
            const colorStyle = isNegative ? { color: 'red' } : {};
            const formattedValue = typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: setting.DISPLAYED_DECIMALS }) : value;
            const displayValue = isTotalRow ? formattedValue : column.percent ? `${formattedValue}%` : formattedValue; // Do not add '%' if it's a totalRow
            return (
                <div style={colorStyle}>
                    {displayValue}
                </div>
            );
        },
        resizable: true,
        sortable: column.sortable,
        sortFunction: column.sortable ? (a, b) => {
            const aValue = a[column.columnName];
            const bValue = b[column.columnName];

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return aValue.localeCompare(bValue);
            } else if (typeof aValue === 'number' && typeof bValue === 'number') {
                return aValue - bValue;
            } else {
                return 0;
            }
        } : undefined,
        wrap: true,
        right: column.align === 'right' ? true : false,
        left: column.align === 'left' ? true : false,
        center: column.align === 'center' ? true : false,
        width: column.width || setting.DEFAULTCOLUMN_WIDTH
    }));

    const csvHeaders = tableConfig.binding.data.columns.map(column => ({
        label: column.name,
        key: column.columnName,
    }));

    const actualDataWithoutTotalItems = (actualData || []).map(row => {
        const filtered = {};
        csvHeaders.forEach(col => {
            filtered[col.key] = row[col.key];
        });

        return filtered;
    });

    const handleCopyToClipboard = () => {
        const exportData = tableConfig.binding.data?.pagination?.show
            ? actualDataWithoutTotalItems
            : tableDataWithTotals;

        const headers = tableConfig.binding.data.columns.map(col => col.name).join("\t"); // Get the headers
        
        const rows = exportData.map(row =>
            tableConfig.binding.data.columns
                    .map(col => {
                        const value = row[col.columnName];

                        if (value === null || value === undefined) {
                            return '';
                        }

                        return value;
                    })
                    .join("\t")
        );

        const dataString = [headers, ...rows].join("\n");

        navigator.clipboard.writeText(dataString).catch(err => {
            console.error('Failed to copy data:', err);
        });
    };

    const customStyle = {
        ...tableOptions.customStyle,
        headRow: {
            style: {
                backgroundColor: chartPalettes[0].ranges[0],
                border: `0.0625rem solid ${chartPalettes[0].ranges[1]}`,
            }
        },
        rows: {
            style: {
                ...tableOptions.customStyle.rows.style,
                border: `0.0625rem solid ${chartPalettes[0].ranges[1]}`,
            },
            highlightOnHoverStyle: {
                backgroundColor: chartPalettes[0].ranges[2],
                color: chartPalettes[0].ranges[4],
            }
        }
    };

    const conditionalRowStyles = [
        {
            when: row => row === subTotalsRow,
            style: {
                fontFamily: 'Lato-Bold',
                backgroundColor: chartPalettes[0].ranges[1],
                color: chartPalettes[0].ranges[4],
                position: 'absolute',
                marginBottom: '-3.125rem',
                bottom: 0,
                zIndex: 1
            },
            classNames: ['data-table-row-subtotals']
        },
        {
            when: row => row === totalsRow,
            style: {
                fontFamily: 'Lato-Bold',
                backgroundColor: chartPalettes[0].ranges[3],
                color: chartPalettes[0].ranges[4],
                position: 'absolute',
                marginBottom: tableConfig.binding.data?.subtotal ? '-6.250rem' : '-3.125rem',
                bottom: 0,
                zIndex: 1,
            },
            classNames: ['data-table-row-totals']
        },
    ];

    const handleRowClick = (row, event) => {
        const rowIndex = tableData.findIndex(dataRow => dataRow === row);

        if (row === totalsRow || row === subTotalsRow) {
            return;
        }

        const title = tableConfig.binding.context.point.onClick.selectedValueColumn;
        const modalTitle = tableConfig.binding.context.point.onClick.selectedTitle;
        const selectedDesc = tableData[rowIndex][tableConfig.binding.data.title];
        const selectedVal = tableData[rowIndex][title];
        const selectedTitle = tableConfig.title;

        if (tableConfig.binding.context.point.onClick.visual || Array.isArray(tableConfig.binding.context.point.onClick.visuals)) {
            let selectedVisual = null;
            if (tableConfig.binding.context.point.onClick.visuals) {
                selectedVisual = tableConfig.binding.context.point.onClick.visuals;
            } else {
                selectedVisual = tableConfig.binding.context.point.onClick.visual;
            }

            setSelectedVisual(selectedVisual);
            setSelectedData({ title, selectedDesc, selectedVal, selectedTitle, modalTitle });

            if (event) {
                let posX = event.clientX;
                let posY = event.clientY;
                if ((event.clientX + setting.modalMinWidth) > window.innerWidth) {
                    posX = event.clientX - setting.modalMinWidth;
                }
                if ((event.clientY + setting.modalMinHeight) > window.innerHeight) {
                    posY = event.clientY - setting.modalMinHeight;
                }
                setModalPosition({ x: posX, y: posY });
            }
        }
    };

    const cursorClass = tableConfig.binding.context.point.onClick.visual || Array.isArray(tableConfig.binding.context.point.onClick.visuals)
        ? 'tauri-datatable-pointer-cursor'
        : 'tauri-datatable-default-cursor';

    return (
        <div>
            <div className='chart-content'>
                <div className='advanced-table-container'>

                    <div className='export-field'>
                        <Dropdown isOpen={exportToggleOpen} toggle={exportToggle} className='export-table-toolbar'>
                            <DropdownToggle tag="span">
                                <img src={process.env.PUBLIC_URL + "/Images/charm--menu-hamburger.svg"} className='dropdown-img' alt="Menu Icon" />
                            </DropdownToggle>

                            <DropdownMenu right>
                                <DropdownItem onClick={handleCopyToClipboard}>Copy to clipboard</DropdownItem>
                                <DropdownItem>
                                    <CSVLink 
                                        className='csv-download-link' 
                                        data={
                                            tableConfig.binding.data?.pagination?.show
                                            ? actualDataWithoutTotalItems
                                            : tableDataWithTotals
                                        } 
                                        headers={csvHeaders} 
                                        filename={`${tableConfig.title}.csv`}
                                    >
                                        Download CSV
                                    </CSVLink>
                                </DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                    </div>

                    <DataTable
                        className={`tauri-datatable ${cursorClass}`}
                        columns={columns}
                        data={tableDataWithTotals}
                        onRowClicked={handleRowClick}
                        customStyles={customStyle}
                        conditionalRowStyles={conditionalRowStyles}
                        highlightOnHover={tableOptions.highlightOnHover}
                        pointerOnHover={tableOptions.pointerOnHover}
                        striped={tableOptions.striped}
                        fixedHeader={tableOptions.fixedHeader}
                        responsive={tableOptions.responsive}
                        expandOnRowClicked={tableOptions.expandOnRowClicked}
                        expandableRows={tableOptions.expandableRows}
                        expandableRowsHideExpander={tableOptions.expandableRowsHideExpander}
                        pagination={tableOptions.pagination}
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                        paginationComponentOptions={{
                            rowsPerPageText: 'Rows per page:',
                            rangeSeparatorText: 'of',
                            noRowsPerPage: false,
                            selectAllRowsItem: false,
                            selectAllRowsItemText: 'All'
                        }}
                    />
                    {tableConfig.binding.data?.pagination?.show && (
                        <TauriCustomPaganation
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={onPageChange}
                            displayPerPage={displayPerPage}
                            onDisplayPerPage={onDisplayPerPage}
                            totalItems={totalItems}
                        />
                    )}
                </div>

            </div>
            {selectedVisual && (
                tableConfig.binding.context.point.onClick.visuals ? (
                    <TauriSectionModalMultiVisual
                        show={selectedVisual !== null}
                        onHide={() => setSelectedVisual(null)}
                        selectedData={selectedData}
                        visual={selectedVisual}
                        columnSize={columnSize}
                        rowSize={rowSize}
                        style={{ top: modalPosition.y, left: modalPosition.x }}
                        palettes={palettes}
                        themes={themes}
                    />
                ) : (
                    <TauriSectionModalVisual
                        show={selectedVisual !== null}
                        onHide={() => setSelectedVisual(null)}
                        selectedData={selectedData}
                        visual={selectedVisual}
                        columnSize={columnSize}
                        rowSize={rowSize}
                        style={{ top: modalPosition.y, left: modalPosition.x }}
                        palettes={palettes}
                        themes={themes}
                    />
                )
            )}
        </div>
    );
};

export default TauriAdvancedTableVisual;
